import { useState, useEffect } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import API from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Trash2, Palette, Ruler, Shirt } from "lucide-react";

export default function AdminMasterVariants() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState("styles");

  const [styles, setStyles] = useState([]);
  const [colors, setColors] = useState([]);
  const [sizes, setSizes] = useState([]);

  const [newStyle, setNewStyle] = useState("");
  const [newColor, setNewColor] = useState("");
  const [newColorHex, setNewColorHex] = useState("#000000");
  const [newSize, setNewSize] = useState("");
  const [adding, setAdding] = useState(false);

  const fetchAll = async () => {
    try {
      const [s, c, z] = await Promise.all([
        API.get("/master/styles"),
        API.get("/master/colors"),
        API.get("/master/sizes"),
      ]);
      setStyles(s.data.styles || []);
      setColors(c.data.colors || []);
      setSizes(z.data.sizes || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleAddStyle = async () => {
    if (!newStyle.trim()) return;
    setAdding(true);
    try {
      const res = await API.post("/master/styles", { name: newStyle.trim(), sort_order: styles.length + 1 });
      if (res.data.already_exists) {
        toast.info("Style sudah ada");
      } else {
        toast.success(t("admin_master.added"));
      }
      setNewStyle("");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setAdding(false); }
  };

  const handleAddColor = async () => {
    if (!newColor.trim()) return;
    setAdding(true);
    try {
      const res = await API.post("/master/colors", { name: newColor.trim(), hex_code: newColorHex, sort_order: colors.length + 1 });
      if (res.data.already_exists) {
        toast.info("Color sudah ada");
      } else {
        toast.success(t("admin_master.added"));
      }
      setNewColor("");
      setNewColorHex("#000000");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setAdding(false); }
  };

  const handleAddSize = async () => {
    if (!newSize.trim()) return;
    setAdding(true);
    try {
      const res = await API.post("/master/sizes", { name: newSize.trim(), sort_order: sizes.length + 1 });
      if (res.data.already_exists) {
        toast.info("Size sudah ada");
      } else {
        toast.success(t("admin_master.added"));
      }
      setNewSize("");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setAdding(false); }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm(t("admin_master.confirm_delete"))) return;
    try {
      await API.delete(`/master/${type}/${id}`);
      toast.success(t("admin_master.deleted"));
      fetchAll();
    } catch { toast.error("Failed to delete"); }
  };

  const tabs = [
    { key: "styles", label: t("admin_master.tab_styles"), icon: Shirt, count: styles.length },
    { key: "colors", label: t("admin_master.tab_colors"), icon: Palette, count: colors.length },
    { key: "sizes", label: t("admin_master.tab_sizes"), icon: Ruler, count: sizes.length },
  ];

  return (
    <AdminLayout title={t("admin_master.title")}>
      <div data-testid="admin-master-variants">
        {/* Tab Navigation */}
        <div className="flex gap-1 mb-6 bg-white border border-stone-200 rounded-lg p-1 w-fit">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all ${
                  isActive
                    ? "bg-[#8A9A86] text-white shadow-sm"
                    : "text-stone-500 hover:text-stone-700 hover:bg-stone-50"
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${isActive ? "bg-white/20 text-white" : "bg-stone-100 text-stone-500"}`}>
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Styles Tab */}
        {activeTab === "styles" && (
          <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
            <div className="p-4 border-b border-stone-100 bg-stone-50/50">
              <div className="flex gap-2">
                <Input
                  value={newStyle}
                  onChange={(e) => setNewStyle(e.target.value)}
                  placeholder={t("admin_master.placeholder_style")}
                  className="flex-1 max-w-xs"
                  onKeyDown={(e) => e.key === "Enter" && handleAddStyle()}
                />
                <button
                  onClick={handleAddStyle}
                  disabled={adding || !newStyle.trim()}
                  className="bg-[#8A9A86] text-white px-4 py-2 text-sm font-semibold rounded-md hover:bg-[#768573] transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  <Plus className="w-4 h-4" />
                  {adding ? t("admin_master.adding") : t("admin_master.add_btn")}
                </button>
              </div>
            </div>
            {styles.length === 0 ? (
              <p className="text-sm text-stone-400 p-6 text-center">{t("admin_master.no_data")}</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-100">
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-stone-500 font-semibold w-12">#</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-stone-500 font-semibold">{t("admin_master.name")}</th>
                    <th className="text-right p-3 text-xs uppercase tracking-wider text-stone-500 font-semibold w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {styles.map((s, idx) => (
                    <tr key={s.id} className="border-b border-stone-50 hover:bg-stone-50/50">
                      <td className="p-3 text-stone-400">{idx + 1}</td>
                      <td className="p-3 font-medium text-stone-800">
                        <div className="flex items-center gap-2">
                          <Shirt className="w-4 h-4 text-stone-400" />
                          {s.name}
                        </div>
                      </td>
                      <td className="p-3 text-right">
                        <button onClick={() => handleDelete("styles", s.id)} className="text-stone-400 hover:text-red-500 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Colors Tab */}
        {activeTab === "colors" && (
          <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
            <div className="p-4 border-b border-stone-100 bg-stone-50/50">
              <div className="flex gap-2 items-center">
                <Input
                  value={newColor}
                  onChange={(e) => setNewColor(e.target.value)}
                  placeholder={t("admin_master.placeholder_color")}
                  className="flex-1 max-w-xs"
                  onKeyDown={(e) => e.key === "Enter" && handleAddColor()}
                />
                <input
                  type="color"
                  value={newColorHex}
                  onChange={(e) => setNewColorHex(e.target.value)}
                  className="w-10 h-10 border border-stone-200 rounded cursor-pointer"
                  title={t("admin_master.hex_code")}
                />
                <button
                  onClick={handleAddColor}
                  disabled={adding || !newColor.trim()}
                  className="bg-[#8A9A86] text-white px-4 py-2 text-sm font-semibold rounded-md hover:bg-[#768573] transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  <Plus className="w-4 h-4" />
                  {adding ? t("admin_master.adding") : t("admin_master.add_btn")}
                </button>
              </div>
            </div>
            {colors.length === 0 ? (
              <p className="text-sm text-stone-400 p-6 text-center">{t("admin_master.no_data")}</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-100">
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-stone-500 font-semibold w-12">#</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-stone-500 font-semibold">{t("admin_master.name")}</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-stone-500 font-semibold">{t("admin_master.hex_code")}</th>
                    <th className="text-right p-3 text-xs uppercase tracking-wider text-stone-500 font-semibold w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {colors.map((c, idx) => (
                    <tr key={c.id} className="border-b border-stone-50 hover:bg-stone-50/50">
                      <td className="p-3 text-stone-400">{idx + 1}</td>
                      <td className="p-3 font-medium text-stone-800">
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 rounded-full border border-stone-200 flex-shrink-0" style={{ backgroundColor: c.hex_code || "#ccc" }} />
                          {c.name}
                        </div>
                      </td>
                      <td className="p-3 text-stone-500 font-mono text-xs">{c.hex_code || "—"}</td>
                      <td className="p-3 text-right">
                        <button onClick={() => handleDelete("colors", c.id)} className="text-stone-400 hover:text-red-500 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Sizes Tab */}
        {activeTab === "sizes" && (
          <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
            <div className="p-4 border-b border-stone-100 bg-stone-50/50">
              <div className="flex gap-2">
                <Input
                  value={newSize}
                  onChange={(e) => setNewSize(e.target.value)}
                  placeholder={t("admin_master.placeholder_size")}
                  className="flex-1 max-w-xs"
                  onKeyDown={(e) => e.key === "Enter" && handleAddSize()}
                />
                <button
                  onClick={handleAddSize}
                  disabled={adding || !newSize.trim()}
                  className="bg-[#8A9A86] text-white px-4 py-2 text-sm font-semibold rounded-md hover:bg-[#768573] transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  <Plus className="w-4 h-4" />
                  {adding ? t("admin_master.adding") : t("admin_master.add_btn")}
                </button>
              </div>
            </div>
            {sizes.length === 0 ? (
              <p className="text-sm text-stone-400 p-6 text-center">{t("admin_master.no_data")}</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-100">
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-stone-500 font-semibold w-12">#</th>
                    <th className="text-left p-3 text-xs uppercase tracking-wider text-stone-500 font-semibold">{t("admin_master.name")}</th>
                    <th className="text-right p-3 text-xs uppercase tracking-wider text-stone-500 font-semibold w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {sizes.map((s, idx) => (
                    <tr key={s.id} className="border-b border-stone-50 hover:bg-stone-50/50">
                      <td className="p-3 text-stone-400">{idx + 1}</td>
                      <td className="p-3 font-medium text-stone-800">
                        <div className="flex items-center gap-2">
                          <Ruler className="w-4 h-4 text-stone-400" />
                          {s.name}
                        </div>
                      </td>
                      <td className="p-3 text-right">
                        <button onClick={() => handleDelete("sizes", s.id)} className="text-stone-400 hover:text-red-500 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
